import React, { useEffect, useRef, useState } from 'react'
import { E2EUserPaginatedModel } from '../../../../../../shared/models/E2EUserPaginatedModel'
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown'
import classes from './ProjectCollaboratorAssignmentDropdown.module.scss'
import { Button } from 'primereact/button'
import { createPortal } from 'react-dom'
import { InputText } from 'primereact/inputtext'
import useDebounce from '../../../../../../hooks/useDebounce'
import { ProjectUserSource } from '../ProjectCollaboratorAssignmentDialog'
import GMAWBUsersService from '../../../../../../services/GMAWBUsersService'
import HandlerType from '../../../enums/handler-type'
import { classNames } from 'primereact/utils'

interface ProjectCollaboratorAssignmentDropdownProps {
  setValue: React.Dispatch<React.SetStateAction<E2EUserPaginatedModel | undefined>>
  dropdownOptions: ProjectUserSource
  setDropdownOptions: React.Dispatch<React.SetStateAction<ProjectUserSource>>
  value: E2EUserPaginatedModel | undefined
  handlerType: HandlerType
  isGmaTcTask?: boolean
  isDisabled: boolean
  onBlur?: (e: any) => void
  className?: string
  showClear?: boolean
  includeCOHandlers?: boolean
  removeAppendToSelf?: boolean
}

interface DropdownValue {
  label: string
  item: string
  value: string
  email: string
}

const ProjectCollaboratorAssignmentDropdown = (
  props: ProjectCollaboratorAssignmentDropdownProps
) => {
  const dropdownRef = useRef<any>(null)
  const [showMoreVisible, setShowMoreVisible] = useState<boolean>(true)
  const [searchCriteria, setSearchCriteria] = useState<string>(
    props.dropdownOptions.currentSearchPhrase
  )
  const [loadingItems, setLoadingItems] = useState<boolean>(false)
  const [overlayRef, setOverlayRef] = useState<any>(null)

  useEffect(() => {
    execSearchInput()
  }, [searchCriteria])

  const getUser = async (
    currentSkip: number,
    currentTake: any,
    handlerType: any,
    isGmaTcTask: any,
    currentSearchPhrase: any
  ) => {
    if (props.includeCOHandlers) {
      const users = await GMAWBUsersService.getCOHandlerPaginatedUsers(
        currentSkip,
        currentTake,
        handlerType,
        isGmaTcTask,
        currentSearchPhrase
      )
      return users
    } else {
      const users = await GMAWBUsersService.getPaginatedUsers(
        currentSkip,
        currentTake,
        handlerType,
        isGmaTcTask,
        currentSearchPhrase
      )
      return users
    }
  }

  const execSearchInput = useDebounce(async () => {
    if (props.dropdownOptions.currentSearchPhrase === searchCriteria) return

    setLoadingItems(true)
    let { currentSkip, currentSearchPhrase } = props.dropdownOptions

    currentSkip = 0
    currentSearchPhrase = searchCriteria

    const users = await getUser(
      currentSkip,
      props.dropdownOptions.currentTake,
      props.handlerType,
      props.isGmaTcTask,
      currentSearchPhrase
    )

    let userData: E2EUserPaginatedModel[] = structuredClone(users.data)

    setShowMoreVisible(userData.length >= props.dropdownOptions.currentTake)
    if (props.value && props.value.displayName && props.value.normalizedEmail) {
      if (userData.some(data => data.normalizedEmail === props.value?.normalizedEmail)) {
        userData = userData.filter(data => data.normalizedEmail !== props.value?.normalizedEmail)
      }

      userData.unshift({
        displayName: props.value.displayName,
        normalizedEmail: props.value.normalizedEmail,
      })
    }

    props.setDropdownOptions(current => ({
      ...current,
      currentSkip: currentSkip,
      currentSearchPhrase: currentSearchPhrase,
      data: userData,
    }))

    setLoadingItems(false)
  }, 800)

  const onShowMoreClick = async () => {
    setLoadingItems(true)
    let { currentSkip, currentTake } = props.dropdownOptions

    currentSkip += 5
    currentTake = 5

    const getUsersData = await getUser(
      currentSkip,
      props.dropdownOptions.currentTake,
      props.handlerType,
      props.isGmaTcTask,
      props.dropdownOptions.currentSearchPhrase
    )

    let userData: E2EUserPaginatedModel[] = structuredClone(getUsersData.data)

    setShowMoreVisible(userData.length >= currentTake)

    if (props.value) {
      if (userData.some(data => data.normalizedEmail === props.value?.normalizedEmail)) {
        userData = userData.filter(data => data.normalizedEmail !== props.value?.normalizedEmail)
      }
    }
    props.setDropdownOptions(current => ({
      ...current,
      currentSkip: currentSkip,
      currentTake: currentTake,
      data: current.data.concat(getUsersData.data),
    }))

    setLoadingItems(false)
  }

  const onDropdownSelect = (e: DropdownChangeParams) => {
    if (!e.value) return props.setValue(e.value)

    const getE2EUser = props.dropdownOptions.data.find(
      user => user.normalizedEmail?.toLowerCase() === e.value.toLowerCase()
    )!
    let dropdownData = structuredClone(props.dropdownOptions.data)

    if (dropdownData.some(data => data.normalizedEmail === getE2EUser.normalizedEmail)) {
      dropdownData = props.dropdownOptions.data.filter(
        data => data.normalizedEmail !== getE2EUser.normalizedEmail
      )
    }

    dropdownData.unshift({
      displayName: getE2EUser.displayName,
      normalizedEmail: getE2EUser.normalizedEmail,
    })

    props.setDropdownOptions(current => ({
      ...current,
      data: dropdownData,
    }))

    props.setValue(getE2EUser)
  }

  const InlineElementFooterTemplate = () => {
    return (
      <div className={`${classes['footer']}`}>
        <Button
          className={`p-button-link ${classes['show-more-button']}`}
          disabled={loadingItems}
          label={loadingItems ? 'Loading' : 'Show More'}
          onClick={e => {
            e.stopPropagation()
            onShowMoreClick()
          }}
        />
      </div>
    )
  }

  const InlineElementHeaderTemplate = () => {
    return (
      <div className={`${classes['header']}`}>
        <div className={`p-input-icon-right ${classes['show-more-not-visible']}`}>
          <InputText
            value={searchCriteria}
            onChange={e => setSearchCriteria(e.target.value)}
            placeholder="Search"
            className={``}
            style={{ width: '100%' }}
          />
          <i className="pi pi-search" />
        </div>

        <div>
          <Button
            type={'button'}
            onClick={() => {
              setSearchCriteria('')
            }}
            icon="pi pi-times"
            className={`p-button-rounded p-button-text p-button-plain ${classes['close-button']}`}
          />
        </div>
      </div>
    )
  }

  const onShow = () => {
    setOverlayRef(dropdownRef.current?.getOverlay?.() ?? null)
  }

  const onHide = () => {
    setOverlayRef(null)
  }

  const valueTemplate = (option: DropdownValue) => {
    if (!option?.label || !option) return <div>-- Select --</div>

    return <div>{option.label}</div>
  }

  return (
    <>
      <Dropdown
        appendTo={props.removeAppendToSelf === true ? undefined : 'self'}
        showClear={props.showClear}
        placeholder="-- Select --"
        className={`dropdown-panel ${classNames(props.className)}`}
        panelClassName={`${
          !showMoreVisible
            ? classes['project-collaborator-dropdown-panel-show-more-not-visible']
            : classes['project-collaborator-dropdown-panel']
        }`}
        ref={dropdownRef}
        value={props.value?.normalizedEmail}
        disabled={props.isDisabled}
        optionDisabled={() => loadingItems}
        options={props.dropdownOptions?.data?.map(option => {
          return {
            label: option.displayName,
            item: `${option.displayName} (${option.normalizedEmail})`,
            value: option.normalizedEmail,
            email: option.normalizedEmail,
          } as DropdownValue
        })}
        itemTemplate={option => option.item}
        valueTemplate={valueTemplate}
        onChange={onDropdownSelect}
        onShow={onShow}
        onHide={onHide}
        onBlur={props.onBlur}
      />
      {overlayRef &&
        createPortal(
          <div className="p-dropdown-custom-header">{InlineElementHeaderTemplate()}</div>,
          overlayRef
        )}
      {overlayRef && showMoreVisible && createPortal(<InlineElementFooterTemplate />, overlayRef)}
    </>
  )
}

export default ProjectCollaboratorAssignmentDropdown
